<script setup lang="ts">
/**
 * One person: their name, their role, their PIN, whether they still work here.
 *
 * A PIN is typed here once and never shown again — the server keeps a hash and
 * each till mints its own verifier. For somebody new it is required; for
 * somebody existing it is left empty unless it is being reset.
 */
import { computed, reactive } from "vue";
import type { StaffRow } from "../api";

const props = defineProps<{
  /** Null adds somebody new. */
  person: StaffRow | null;
  /** The roles this person may hand out: never above their own. */
  roles: readonly string[];
  t: (key: string) => string;
  busy: boolean;
  error: string | null;
}>();

const emit = defineEmits<{
  save: [change: { displayName: string; roleId: string; active: boolean; pin: string | null }];
  cancel: [];
}>();

const form = reactive({
  displayName: props.person?.displayName ?? "",
  roleId: props.person?.roleId ?? "staff",
  active: props.person?.active ?? true,
  pin: "",
});

const roleName = (roleId: string): string => {
  const key = `role.${roleId}`;
  const name = props.t(key);
  return name === key ? roleId : name;
};

const pinOk = computed(() => (form.pin === "" ? props.person !== null : /^\d{6,12}$/.test(form.pin)));
const valid = computed(() => form.displayName.trim().length > 0 && pinOk.value);

const save = (): void => {
  if (!valid.value) return;
  emit("save", {
    displayName: form.displayName.trim(),
    roleId: form.roleId,
    active: form.active,
    pin: form.pin === "" ? null : form.pin,
  });
};
</script>

<template>
  <aside class="gf-drawer sheet" role="dialog" aria-modal="false" :aria-label="person?.displayName ?? t('staff.add')">
    <button type="button" class="gf-drawer__close" :aria-label="t('menu.cancel')" @click="emit('cancel')">×</button>
    <h2 class="gf-drawer__title">{{ person ? person.displayName : t("staff.add") }}</h2>

    <form class="sheet__form" @submit.prevent="save">
      <label class="gf-field">
        <span class="gf-label">{{ t("staff.name") }}</span>
        <input v-model="form.displayName" class="gf-input" type="text" maxlength="60" data-field="name" />
        <span class="sheet__hint">{{ t("staff.nameHint") }}</span>
      </label>

      <label class="gf-field">
        <span class="gf-label">{{ t("staff.role") }}</span>
        <select v-model="form.roleId" class="gf-input" data-field="role">
          <option v-for="r in roles" :key="r" :value="r">{{ roleName(r) }}</option>
        </select>
      </label>

      <label class="gf-field">
        <span class="gf-label">{{ person ? t("staff.newPin") : t("staff.pin") }}</span>
        <input
          v-model="form.pin"
          class="gf-input gf-num"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          maxlength="12"
          data-field="pin"
        />
        <span class="sheet__hint" :class="{ 'sheet__hint--bad': form.pin !== '' && !pinOk }">
          {{ person ? t("staff.pinResetHint") : t("staff.pinHint") }}
        </span>
      </label>

      <label v-if="person" class="sheet__check">
        <input v-model="form.active" type="checkbox" data-field="active" />
        {{ t("staff.active") }}
      </label>

      <p v-if="error" class="sheet__error" role="alert">{{ error }}</p>

      <div class="sheet__actions">
        <button type="button" class="gf-button" @click="emit('cancel')">{{ t("menu.cancel") }}</button>
        <button type="submit" class="gf-button gf-button--primary" :disabled="!valid || busy" data-testid="save-staff">
          {{ t("menu.save") }}
        </button>
      </div>
    </form>
  </aside>
</template>

<style scoped>
.sheet {
  width: min(28rem, 100vw);
}

.sheet__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
}

.sheet__hint {
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.sheet__hint--bad {
  color: var(--gf-color-danger);
}

.sheet__check {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.sheet__error {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-danger-surface);
  color: var(--gf-color-text);
}

.sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
  padding-top: var(--gf-space-4);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}
</style>
